import * as DActions from 'ApplicationEntry/ducks/ProductsRiders/disability';
import * as D from 'ApplicationEntry/models/ProductsRiders/Disability/NewYorkDisability';
import * as ErrorMessages from '../../ErrorMessages';
import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import './../ProductRiders.sass';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {ProductRiderGroups, ProductsRidersPolicyType} from 'ApplicationEntry/models/ProductsRiders/Families';

type DisabilityProps = {
  disability: D.NewYorkDisability;
  product: string;
};

const Disability = ({disability, product}: DisabilityProps) => {
  const {
    plan,
    premium,
    cwa,
    section125,
    monthlySalary,
    sickness,
    sicknesses,
    occClass,
    occClasses,
    accident,
    accidents,
    jetIssue,
    type,
    monthlyBenefit,
    benefitPeriod,
    periods,
    planList,
  } = disability;
  const dispatch = useDispatch();
  const [typeBlurred, setBlurType] = useState(false);
  const [planBlurred, setBlurPlan] = useState(false);
  const showTypeError = typeBlurred && type.key === 'Empty';
  const showPlanError = planBlurred && plan.key === 'Empty';
  const [typeLabel, setTypeLabel] = useState<HTMLLabelElement | null>();
  const [planLabel, setPlanLabel] = useState<HTMLLabelElement | null>();
  const [accidentLabel, setAccidentLabel] = useState<HTMLLabelElement | null>();
  const [sicknessLabel, setSicknessLabel] = useState<HTMLLabelElement | null>();
  const [periodLabel, setPeriodLabel] = useState<HTMLLabelElement | null>();

  useEffect(() => {
    dispatch(DActions.changeProduct(product));
  }, [dispatch, product]);

  return (
    <>
      <div className="product-riders-section__policy-type" id="disability-section">
        <form noValidate autoComplete="off" className="product-riders-section__policy-type__form">
          <div className="product-riders-section__policy-type__form__row">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <FormControl
                  error={showPlanError}
                  variant="outlined"
                  className="product-riders-section__policy-type__form__row--field"
                >
                  <InputLabel ref={setPlanLabel} id="disability-plan-label">
                    Plan
                  </InputLabel>
                  <Select
                    labelId="disability-label-plan"
                    labelWidth={planLabel?.clientWidth}
                    id="disability-plan"
                    value={plan.key === 'Empty' ? '' : plan.value}
                    onBlur={() => setBlurPlan(true)}
                    onChange={event => dispatch(DActions.changePlan(event.target.value as string))}
                    displayEmpty
                  >
                    {planList?.map((plan: string) => (
                      <MenuItem value={plan} key={plan}>
                        {plan}
                      </MenuItem>
                    ))}
                  </Select>
                  {showPlanError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl
                  error={showTypeError}
                  variant="outlined"
                  className="product-riders-section__policy-type__form__row--field"
                >
                  <InputLabel ref={setTypeLabel} id="disability-type-label">
                    Type
                  </InputLabel>
                  <Select
                    labelId="disability-label-type"
                    labelWidth={typeLabel?.clientWidth}
                    id="disability-type"
                    value={type.key === 'Empty' ? '' : type.value}
                    onBlur={() => setBlurType(true)}
                    onChange={event => {
                      dispatch(DActions.changeType(event.target.value as string));
                    }}
                  >
                    {D.types[product].map((type: string) => (
                      <MenuItem value={type} key={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {showTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="checkedB"
                      color="secondary"
                      id="disability-section-125"
                      checked={section125}
                      onChange={() => dispatch(DActions.changeSection125(!section125))}
                    />
                  }
                  label={<Typography>Section 125</Typography>}
                  className="product-riders-section__policy-type__form__row--field"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="checkedB"
                      color="secondary"
                      id="disability-jet-issue"
                      checked={jetIssue}
                      onChange={() => dispatch(DActions.changeJetIssue(!jetIssue))}
                    />
                  }
                  label={<Typography>Jet Issue</Typography>}
                  className="product-riders-section__policy-type__form__row--field"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  id="disability-premium"
                  label="Premium"
                  variant="outlined"
                  placeholder="Enter Premium"
                  margin="normal"
                  fullWidth
                  className="product-riders-section__policy-type__form__row--field"
                  value={premium.value}
                  required={section125}
                  onChange={event => dispatch(DActions.changePremium(event.target.value as string))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="disability-cwa"
                  label="CWA"
                  variant="outlined"
                  placeholder="Enter CWA"
                  margin="normal"
                  fullWidth
                  className="product-riders-section__policy-type__form__row--field"
                  value={cwa.value}
                  onChange={event => dispatch(DActions.changeCWA(event.target.value as string))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="disability-monthly-salary"
                  label="Monthly Salary"
                  variant="outlined"
                  placeholder="Enter Monthly Salary"
                  margin="normal"
                  fullWidth
                  className="product-riders-section__policy-type__form__row--field"
                  value={monthlySalary.value}
                  onChange={event => dispatch(DActions.changeMonthlySalary(event.target.value as string))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                  <InputLabel ref={setAccidentLabel} id="disability-accident-label">
                    Accident
                  </InputLabel>
                  <Select
                    labelId="disability-label-accident"
                    labelWidth={accidentLabel?.clientWidth}
                    id="disability-accident"
                    value={accident.key === 'Empty' || accident.value === undefined ? '' : accident.value}
                    onChange={event => dispatch(DActions.changeAccident(event.target.value as string))}
                    displayEmpty
                    disabled={product === ProductRiderGroups[ProductsRidersPolicyType.DitWuNY]}
                  >
                    {accidents?.map((accident: string) => (
                      <MenuItem value={accident} key={accident}>
                        {accident}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                  <InputLabel ref={setSicknessLabel} id="disability-sickness-label">
                    Sickness
                  </InputLabel>
                  <Select
                    labelId="disability-label-sickness"
                    labelWidth={sicknessLabel?.clientWidth}
                    id="disability-sickness"
                    value={sickness.key === 'Empty' ? '' : sickness.value}
                    onChange={event => dispatch(DActions.changeSickness(event.target.value as string))}
                    displayEmpty
                    disabled={product === ProductRiderGroups[ProductsRidersPolicyType.DitWuNY]}
                  >
                    {sicknesses?.map((sickness: string) => (
                      <MenuItem value={sickness} key={sickness}>
                        {sickness}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                  <InputLabel ref={setPeriodLabel} id="disability-period-label">
                    Period
                  </InputLabel>
                  <Select
                    labelId="disability-label-period"
                    labelWidth={periodLabel?.clientWidth}
                    id="disability-period"
                    value={benefitPeriod.key === 'Empty' ? '' : benefitPeriod.value}
                    onChange={event => dispatch(DActions.changeBenefitPeriod(event.target.value as string))}
                    displayEmpty
                    disabled={product === ProductRiderGroups[ProductsRidersPolicyType.DitWuNY]}
                  >
                    {periods?.map((period: number) => (
                      <MenuItem value={period} key={period}>
                        {period}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="disability-monthly"
                  label="Monthly"
                  variant="outlined"
                  placeholder="Enter Monthly"
                  margin="normal"
                  fullWidth
                  className="product-riders-section__policy-type__form__row--field"
                  value={monthlyBenefit.value}
                  onChange={event => dispatch(DActions.changeBenefitMonthly(event.target.value as string))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                  <InputLabel ref={setTypeLabel} id="disability-occ-class-label">
                    Occ. Class
                  </InputLabel>
                  <Select
                    labelId="disability-label-occ-class"
                    labelWidth={typeLabel?.clientWidth}
                    id="disability-occ-class"
                    value={occClass.key === 'Empty' ? '' : occClass.value}
                    onChange={event => dispatch(DActions.changeOccClass(event.target.value as string))}
                    displayEmpty
                    disabled={product === ProductRiderGroups[ProductsRidersPolicyType.DitWuNY]}
                  >
                    {occClasses?.map((occClass: string) => (
                      <MenuItem value={occClass} key={occClass}>
                        {occClass}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </div>
        </form>
      </div>
    </>
  );
};

export default Disability;
